import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Chip, Grid, Tooltip, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import NotificationsIcon from '@material-ui/icons/Notifications';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import clsx from 'clsx';
import { format, isPast } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import DialogViewWrapper from 'components/DialogViewWrapper';
import FormTitle from 'components/FormTitle';
import InformationSummary from 'components/InformationSummary';
import Loader from 'components/Loader';
import RichTextContent from 'components/RichTextContent/RichTextContent';
import actionInstanceStepApi from 'config/api/actionInstanceStep/actionInstanceStep';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { ActionLibraryInstance, ActionLibraryInstanceStepStatus } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import checkAccess from 'config/permissions/checkAccess';
import RolesOrganization from 'config/permissions/RolesOrganization';
import { translatedOrFallback } from 'helpers/safeT/safeT';
import action_library_messages from 'messages/action_library_messages';
import assets_messages from 'messages/assets_messages';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import ActionLibraryInstanceActions from './_components/ActionLibraryInstanceActions/ActionLibraryInstanceActions';
import ActionLibraryInstanceDetailsSteps from './_components/ActionLibraryInstanceDetailsSteps/ActionLibraryInstanceDetailsSteps';
import ActionLibraryInstanceProgress from './_components/ActionLibraryInstanceProgress/ActionLibraryInstanceProgress';
import CompleteInstanceDialog from './_components/CompleteInstanceDialog/CompleteInstanceDialog';
import InstanceCommentsDialog from './_components/InstanceCommentsDialog';
import InstanceCommentsSection from './_components/InstanceCommentsSection';
import InstanceStatusLogAccordion from './_components/InstanceStatusLogAccordion';
import ReopenInstanceDialog from './_components/ReopenInstanceDialog/ReopenInstanceDialog';
import useStyles from './ActionLibraryInstanceDetailPage.styles';

const ActionLibraryInstanceDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [instance, setInstance] = useState<ActionLibraryInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [instanceCommentsOpen, setInstanceCommentsOpen] = useState(false);
  const styles = useStyles();

  const loadInstance = React.useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await actionLibraryInstancesApi.getSingleActionLibraryInstance(Number(id));
      setInstance(data);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const patchStepMutation = useMutation(
    ({ stepId, status }: { stepId: number; status: ActionLibraryInstanceStepStatus }) =>
      actionInstanceStepApi.patchStep(stepId, { status }),
    {
      onSuccess: async result => {
        queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL');
        const updatedStep = result.data;
        setInstance(prev => {
          if (!prev?.steps) return prev;
          return {
            ...prev,
            steps: prev.steps.map(s =>
              s.id === updatedStep.id
                ? {
                    ...s,
                    status: updatedStep.status as ActionLibraryInstanceStepStatus,
                    statusDisplay: updatedStep.statusDisplay,
                    blockReason: updatedStep.blockReason ?? s.blockReason,
                    completionComment: updatedStep.completionComment ?? s.completionComment,
                  }
                : s,
            ),
          };
        });
        if (id) {
          const { data } = await actionLibraryInstancesApi.getSingleActionLibraryInstance(Number(id));
          setInstance(prev =>
            prev
              ? {
                  ...prev,
                  statusPercent: data.statusPercent,
                  status: data.status,
                  statusDisplay: data.statusDisplay,
                  canComplete: data.canComplete,
                  completeButtonTooltip: data.completeButtonTooltip,
                  updatedAt: data.updatedAt,
                }
              : null,
          );
        }
      },
      onError: () => {
        enqueueSnackbar(t(action_library_messages.errors.instance_create_failed), { variant: 'error' });
      },
    },
  );

  useEffect(() => {
    if (id) loadInstance();
  }, [id, loadInstance]);

  const handleBack = () => {
    history.push(PATHS.ACTION_LIBRARY_INSTANCE);
  };

  const handleDelete = () => {
    history.push(PATHS.ACTION_LIBRARY_INSTANCE);
  };

  const handleStepStatusChange = useCallback(
    (stepId: number, status: ActionLibraryInstanceStepStatus) => {
      patchStepMutation.mutate({ stepId, status });
    },
    [patchStepMutation],
  );

  const userData = userDataStorage.get() ?? {};
  const currentUserId = (userData as { id?: number }).id;
  const isAssetManager = checkAccess(userData.roles ?? [], [RolesOrganization.AssetsManager]);
  const isOwner = instance?.owner != null && currentUserId != null && instance.owner === currentUserId;
  const isDelegee = instance?.delegatedTo != null && currentUserId != null && instance.delegatedTo === currentUserId;
  const isDelegeeOnly = Boolean(isDelegee && !isOwner && !isAssetManager);

  const canComplete = Boolean((instance?.canComplete ?? false) && (isOwner || isAssetManager) && !isDelegeeOnly);

  const completeDisabledTooltip = useMemo(() => {
    if (!instance) return '';
    if (isDelegeeOnly) return t(action_library_messages.instance_actions.complete_disabled_delegatee_cannot_complete);
    return instance.completeButtonTooltip || '';
  }, [instance, isDelegeeOnly, t]);

  const informationSummaryData = useMemo(() => {
    if (!instance) return [];

    const deadlineDate = instance.deadline ? new Date(instance.deadline) : null;
    const reminderDate = instance.reminder ? new Date(instance.reminder) : null;
    const isDeadlinePast = deadlineDate && isPast(deadlineDate) && instance.status !== 'completed';
    const isReminderPast = reminderDate && isPast(reminderDate) && instance.status !== 'completed';

    // Status with icon
    let StatusIconComponent;
    let statusIconClass;
    if (instance.status === 'completed') {
      StatusIconComponent = CheckCircleIcon;
      statusIconClass = styles.activeIcon;
    } else if (instance.status === 'in_progress') {
      StatusIconComponent = RemoveCircleOutlineIcon;
      statusIconClass = styles.criticalityMediumIcon;
    } else {
      StatusIconComponent = CancelIcon;
      statusIconClass = styles.inactiveIcon;
    }

    // Criticality with icon
    let CriticalityIconComponent;
    let criticalityIconClass;
    if (instance.criticality === 'low') {
      CriticalityIconComponent = InfoIcon;
      criticalityIconClass = styles.criticalityLowIcon;
    } else if (instance.criticality === 'medium') {
      CriticalityIconComponent = WarningIcon;
      criticalityIconClass = styles.criticalityMediumIcon;
    } else {
      CriticalityIconComponent = ErrorIcon;
      criticalityIconClass = styles.criticalityHighIcon;
    }

    const criticalityKey = instance.criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';

    return [
      {
        label: action_library_messages.instance.detail.criticality,
        value: (
          <div className={styles.valueWithIcon}>
            <CriticalityIconComponent className={`${styles.statusIcon} ${criticalityIconClass}`} />
            <span>{t(action_library_messages.criticality[criticalityKey])}</span>
          </div>
        ),
      },
      {
        label: action_library_messages.instance.detail.status,
        value: (
          <div className={styles.valueWithIcon}>
            <StatusIconComponent className={`${styles.statusIcon} ${statusIconClass}`} />
            <span>{instance.statusDisplay || instance.status}</span>
          </div>
        ),
      },
      {
        label: action_library_messages.table.area,
        value: ((instance.areaData?.displayName as string) || instance.areaDisplay || instance.area) ?? '-',
      },
      {
        label: action_library_messages.table.subarea,
        value: (() => {
          if (!instance.subAreaData) return (instance.subAreaDisplay || instance.subArea) ?? '-';
          return translatedOrFallback(t, instance.subAreaData.nameTranslationKey, instance.subAreaData.name as string);
        })(),
      },
      {
        label: action_library_messages.instance.detail.template_name,
        value: instance.templateName ?? '–',
      },
      {
        label: action_library_messages.instance.detail.template_source,
        value: instance.templateSourceDisplay ?? instance.templateSource ?? '–',
      },
      {
        label: action_library_messages.instance.detail.asset_name,
        value: instance.assetName || '-',
      },
      {
        label: action_library_messages.instance.detail.asset_type,
        value: instance.assetType || '-',
      },
      {
        label: action_library_messages.instance.detail.asset_importance,
        value: instance.assetImportance
          ? // @ts-ignore
            t(assets_messages.information_choices_assets_importance[instance.assetImportance]) || instance.assetImportance
          : '-',
      },
      {
        label: action_library_messages.instance.detail.owner,
        value: instance.ownerFullName || '-',
      },
      {
        label: action_library_messages.table.responsible_user,
        value:
          instance.responsibleUserFullName ||
          (instance.responsibleUser != null ? String(instance.responsibleUser) : null) ||
          instance.delegatedToFullName ||
          instance.ownerFullName ||
          '-',
      },
      {
        label: action_library_messages.instance.detail.reported_to,
        value: instance.reportedTo ?? '-',
      },
      {
        label: action_library_messages.instance.detail.deadline,
        value: deadlineDate ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '5px' }}>
            <span style={{ color: isDeadlinePast ? '#f44336' : 'inherit', fontWeight: isDeadlinePast ? 'bold' : 'normal' }}>
              {format(deadlineDate, 'yyyy-MM-dd HH:mm')}
            </span>
          </div>
        ) : (
          '-'
        ),
      },
      {
        label: action_library_messages.instance.detail.reminder,
        value: reminderDate ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <NotificationsIcon style={{ fontSize: '1rem', color: isReminderPast ? '#ff9800' : 'rgba(0, 0, 0, 0.54)' }} />
            <span style={{ color: isReminderPast ? '#ff9800' : 'inherit' }}>{format(reminderDate, 'yyyy-MM-dd HH:mm')}</span>
          </div>
        ) : (
          '-'
        ),
      },
    ];
  }, [instance, t, styles]);

  const validityPeriodData = useMemo(() => {
    if (!instance) return [];
    const value =
      instance.validityPeriodMonths != null
        ? t(action_library_messages.instance.detail.validation_period_months, { count: instance.validityPeriodMonths })
        : t(action_library_messages.properties.doesnt_apply);
    return [
      {
        label: action_library_messages.detail.validity_period,
        value,
      },
    ];
  }, [instance, t]);

  const instanceOneColumnData = useMemo(() => {
    if (!instance) return [];

    const referenceValue = instance.reference ?? instance.templateReference ?? null;
    const referenceChips =
      referenceValue && referenceValue.trim() ? (
        <div className={styles.tagsContainer}>
          {referenceValue.split(', ').map((ref, index) => (
            <Tooltip key={index} title={ref}>
              <Chip key={index} className={clsx(styles.chips, styles.tooltipChipTruncated)} label={ref} size='small' />
            </Tooltip>
          ))}
        </div>
      ) : (
        '-'
      );

    const tagsSource = instance.tags ?? instance.templateTags ?? null;
    const tagsChips =
      tagsSource && typeof tagsSource === 'object' && Object.keys(tagsSource).length > 0 ? (
        <div className={styles.tagsContainer}>
          {Object.entries(tagsSource).map(([key, value]) => (
            <Tooltip key={key} title={`${String(value).toLowerCase()}`}>
              <Chip
                key={key}
                className={clsx(styles.chips, styles.tooltipChipTruncated)}
                label={`${String(value).toLowerCase()}`}
                size='small'
              />
            </Tooltip>
          ))}
        </div>
      ) : (
        '-'
      );

    return [
      {
        label: action_library_messages.detail.reference,
        value: referenceChips,
      },
      {
        label: action_library_messages.detail.tags,
        value: tagsChips,
      },
    ];
  }, [instance, styles]);

  if (loading) {
    return (
      <DialogViewWrapper contentSize='lg' onBack={handleBack} title={t(action_library_messages.instance.detail.page_title)}>
        <Loader inner />
      </DialogViewWrapper>
    );
  }

  if (error || !instance) {
    return (
      <DialogViewWrapper contentSize='lg' onBack={handleBack} title={t(action_library_messages.instance.detail.page_title)}>
        <div>{t(action_library_messages.error_state.title)}</div>
      </DialogViewWrapper>
    );
  }

  return (
    <DialogViewWrapper
      contentClassName={instance.status === 'completed' ? styles.completedContentBorder : undefined}
      contentSize='lg'
      onBack={handleBack}
      title={t(action_library_messages.instance.detail.page_title)}
    >
      <div className={styles.stickyMenuContainer}>
        <ActionLibraryInstanceActions
          canComplete={canComplete}
          canReopen={instance.canReopen === true}
          completeDisabledTooltip={completeDisabledTooltip}
          instance={instance}
          onCompleteClick={() => setCompleteDialogOpen(true)}
          onDelete={handleDelete}
          onInstanceUpdate={loadInstance}
          onReopenClick={() => setReopenDialogOpen(true)}
          onReturnToOwnerSuccess={updatedInstance => {
            setInstance(updatedInstance);
            history.push(PATHS.ACTION_LIBRARY_INSTANCE);
          }}
          onTakeBackSuccess={updatedInstance => {
            setInstance(updatedInstance);
          }}
        />
        <CenteredGrid className={styles.stickyProgress} title='' width='lg' withoutPadding>
          <ActionLibraryInstanceProgress hideCompletedBadge status={instance.status} statusPercent={instance.statusPercent ?? 0} />
        </CenteredGrid>
      </div>
      <CenteredGrid gridGap={2} tight width='lg'>
        <Grid container spacing={3}>
          <Grid item md={8} xs={12}>
            <FormTitle
              rightAdornment={
                instance.delegatedTo != null ? (
                  <Chip color='default' label={t(action_library_messages.instance_actions.delegated_state)} size='small' />
                ) : null
              }
            >
              {instance.title}
            </FormTitle>
            <Typography style={{ marginBottom: 8 }} variant='body2'>
              <RichTextContent content={instance.description || '-'} />
            </Typography>
          </Grid>
          <Grid item md={4} xs={12}>
            <InstanceCommentsSection instance={instance} onOpenComments={() => setInstanceCommentsOpen(true)} />
          </Grid>
        </Grid>
      </CenteredGrid>

      <CenteredGrid gridGap={2} tight title='' width='lg'>
        <InformationSummary data={informationSummaryData} />
      </CenteredGrid>

      <CenteredGrid gridGap={2} tight title='' width='lg'>
        <InformationSummary data={instanceOneColumnData} oneColumn />
      </CenteredGrid>

      <CenteredGrid gridGap={2} tight title='' width='lg'>
        <InformationSummary data={validityPeriodData} oneColumn />
      </CenteredGrid>

      <CenteredGrid gridGap={2} tight title={t(action_library_messages.instance.detail.special_instruction)} width='lg'>
        <Typography variant='body2'>
          <RichTextContent content={instance.specialInstruction || '-'} />
        </Typography>
      </CenteredGrid>

      <InstanceStatusLogAccordion instanceId={instance.id} />

      {instance.delegationComment && (
        <CenteredGrid gridGap={2} tight title={t(action_library_messages.instance.detail.delegation_comment)} width='lg'>
          <Typography variant='body2'>
            <RichTextContent content={instance.delegationComment} />
          </Typography>
        </CenteredGrid>
      )}

      {instance.status === 'completed' && instance.completionComment && (
        <CenteredGrid gridGap={2} tight title={t(action_library_messages.instance.detail.completion_comment)} width='lg'>
          <Typography variant='body2'>{instance.completionComment}</Typography>
        </CenteredGrid>
      )}

      {instance.steps && instance.steps.length > 0 && (
        <CenteredGrid gridGap={2} tight title={t(action_library_messages.instance.detail.steps)} width='lg'>
          <ActionLibraryInstanceDetailsSteps
            onStepStatusChange={instance.status === 'completed' ? undefined : handleStepStatusChange}
            readOnly={instance.status === 'completed'}
            steps={instance.steps}
          />
        </CenteredGrid>
      )}

      <CompleteInstanceDialog
        instance={instance}
        onClose={() => setCompleteDialogOpen(false)}
        onSuccess={loadInstance}
        open={completeDialogOpen}
      />
      <ReopenInstanceDialog
        instance={instance}
        onClose={() => setReopenDialogOpen(false)}
        onSuccess={loadInstance}
        open={reopenDialogOpen}
      />
      <InstanceCommentsDialog
        instanceId={instance.id}
        onClose={() => setInstanceCommentsOpen(false)}
        onCommentAdded={loadInstance}
        open={instanceCommentsOpen}
        readOnly={instance.status === 'completed'}
      />
    </DialogViewWrapper>
  );
};

export default ActionLibraryInstanceDetailPage;
